import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {Box, Chip, Typography} from '@mui/material';
import {AppButton, AppIconButton} from 'components/buttons';
import {BaseCard} from 'components/card';
import AppBaseCardHeader from 'components/card/AppBaseCardHeader';
import {useGetAlltimeTrackerEntities} from 'hooks';
import parser from 'html-react-parser';
import {Add, ArrowRight} from 'iconsax-react';
import {formatSecondsToTime, TimeTrackerStatus} from 'models/timeEntry';
import React, {useCallback, useEffect, useState} from 'react';
import {TimerCardSkeleton} from 'views/TimeTracker/components/TimerCardSkeleton';
import TimeTrackerAddUpdateManulEntryDialog from 'views/TimeTracker/TimeTrackerAddUpdateManulEntryDialog';

interface ItemDataTimeTrackerHistoryTabProps {
    itemDataId: string;
}

const ITEMS_PER_PAGE = 10;

export const ItemDataTimeTrackerHistoryTab: React.FC<ItemDataTimeTrackerHistoryTabProps> = ({itemDataId}) => {
    const {
        getAllTimeTrackerEntities,
        getAllTimeTrackerEntitiesLoading: loading,
        getAllTimeTrackerEntitiesResponse,
    } = useGetAlltimeTrackerEntities();

    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const handleOpenCreateDialog = () => {
        setOpenCreateDialog(true);
    };
    const handleCloseCreateDialog = () => {
        setOpenCreateDialog(false);
    };

    useEffect(() => {
        if (itemDataId) {
            getAllTimeTrackerEntities({
                variables: {
                    first: ITEMS_PER_PAGE,
                    where: {
                        itemDataId: {
                            eq: itemDataId,
                        },
                    },
                },
            });
        }
    }, [itemDataId]);

    const handleAfterCreateTimeTrackerEntry = () => {
        if (itemDataId) {
            getAllTimeTrackerEntities({
                variables: {
                    first: ITEMS_PER_PAGE,
                    where: {
                        itemDataId: {
                            eq: itemDataId,
                        },
                    },
                },
            });
        }
    };

    const handleLoadMore = useCallback(() => {
        if (getAllTimeTrackerEntitiesResponse?.pageInfo?.hasNextPage) {
            getAllTimeTrackerEntities({
                variables: {
                    first: ITEMS_PER_PAGE,
                    after: getAllTimeTrackerEntitiesResponse.pageInfo.endCursor,
                    where: {
                        itemDataId: {
                            eq: itemDataId,
                        },
                    },
                },
            });
        }
    }, [getAllTimeTrackerEntitiesResponse]);

    if (loading && !getAllTimeTrackerEntitiesResponse) {
        return (
            <>
                <TimerCardSkeleton />
                <TimerCardSkeleton />
                <TimerCardSkeleton />
            </>
        );
    }

    return (
        <Box id="Time Tracking" sx={{p: 2}}>
            <TimeTrackerAddUpdateManulEntryDialog
                afterUpdateAndCreateHandler={handleAfterCreateTimeTrackerEntry}
                open={openCreateDialog}
                itemDataId={itemDataId}
                onCloseDialog={handleCloseCreateDialog}
            />
            <Box display="flex" alignItems="center" justifyContent={'space-between'} gap={1} mb={2}>
                <Typography variant="h5">Time Tracking History</Typography>
                <AppIconButton
                    isLoading={loading}
                    tooltip="Add manual entry"
                    variant="outlined"
                    onClick={handleOpenCreateDialog}
                >
                    <Add />
                </AppIconButton>
            </Box>
            <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}} />

            {!loading && getAllTimeTrackerEntitiesResponse?.nodes.length === 0 && (
                <Typography variant="body1" color="text.secondary" align="center" sx={{mt: 2}}>
                    No time entries found for this item
                </Typography>
            )}
            {getAllTimeTrackerEntitiesResponse?.nodes.map(item => (
                <BaseCard
                    key={item.id}
                    menus={
                        <Chip
                            label={item.status === TimeTrackerStatus.Completed.toString() ? 'Tracket' : item.status}
                            variant="outlined"
                        />
                    }
                    icon={<AccessTimeIcon color="primary" style={{fontSize: '30px'}} />}
                    headerData={
                        <Box display="flex" alignItems="center" justifyContent={'space-between'} width={'100%'}>
                            <AppBaseCardHeader
                                dateTime={item.createdDate}
                                title={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {item.userInfo?.firstName} {item.userInfo?.lastName}
                                        </Typography>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Spent Time:
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {formatSecondsToTime(item.durationInSeconds)}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    }
                    footerData={
                        <Box>
                            {item.title && (
                                <Typography variant="subtitle1" sx={{fontWeight: 500}}>
                                    {item.title}
                                </Typography>
                            )}
                            {item.description && (
                                <Typography variant="body2" color="text.secondary">
                                    {parser(item.description || '<></>')}
                                </Typography>
                            )}
                        </Box>
                    }
                />
            ))}

            {getAllTimeTrackerEntitiesResponse?.pageInfo.hasNextPage && (
                <Box display="flex" justifyContent="center" mt={2}>
                    <AppButton
                        onClick={handleLoadMore}
                        isLoading={loading}
                        fullWidth
                        endIcon={<ArrowRight size={16} />}
                    >
                        {loading ? 'Loading more entries...' : 'Load More Time Entries'}
                    </AppButton>
                </Box>
            )}
        </Box>
    );
};

export default ItemDataTimeTrackerHistoryTab;
